/** Sites/locations page (artifacts/web/src/pages/sites.tsx) + location dialog + opening-hours editor. */
export const sites = {
  title: "Sites",
  description: "Venues and their player fleet in one place.",
  unassigned: "Unassigned",
  unknown: "Unknown",
  searchPlaceholder: "Search sites or devices…",

  gate: {
    description: "Manage your venues and player fleet.",
    noPermission: "You don't have permission to view sites in this workspace.",
  },

  actions: {
    newLocation: "New location",
    bulkAdd: "Bulk add",
    newDevice: "New device",
    preview: "Preview",
    settings: "Settings",
    move: "Move",
  },

  filters: {
    location: "Location",
    sort: "Sort",
  },

  statusFilter: {
    all: "All statuses",
    idle: "Idle",
    error: "Error",
  },

  locationFilter: {
    all: "All locations",
  },

  sort: {
    nameAsc: "Name (A→Z)",
    nameDesc: "Name (Z→A)",
    oldest: "Oldest first",
    newest: "Newest first",
  },

  error: {
    title: "Couldn't load sites",
    description:
      "Something went wrong while loading your venues and fleet. Check your connection and try again.",
  },

  selectAllDevices: "Select all devices",
  selectAllCount: "Select all ({count})",
  selectAllInGroup: "Select all devices in {group}",
  selectDevice: "Select {name}",

  empty: {
    noDevicesAtSite: "No devices at this site.",
    noMatch: "No devices match your filters.",
    // Guided empty states (M9.4).
    noMatchTitle: "No devices match",
    clearFilters: "Clear filters",
    noLocationsTitle: "Add your first location",
    noLocations:
      "Group your screens by venue or room. Create a location, then add the screens that live there.",
    noDevicesTitle: "Add your first screen",
    noDevices:
      "Add a screen to this workspace, then assign it to a location to start playing.",
  },

  // "Play something now" per-device menu (device-quick-play.tsx).
  quickPlay: {
    aria: "Play something on {name}",
    menuLabel: "Play something now",
    rightNow: "Right now (until next change)",
    noPlaylists: "No playlists this device can play.",
    instantToast: "Playing on {name}",
  },

  // List / now-playing-wall view toggle.
  view: {
    aria: "Fleet view",
    list: "List",
    wall: "Now playing",
  },

  // Now-playing wall (now-playing-wall.tsx).
  wall: {
    paused: "Paused",
    idle: "Nothing playing",
    offlineFor: "Offline {ago}",
    neverConnected: "Never connected",
    unknownTitle: "Unknown",
    empty: {
      title: "Nothing on the wall yet",
      description:
        "Add a device and start playback to see what's playing at every venue, at a glance.",
    },
  },

  pagination: {
    showing: "Showing {from}–{to} of {total}",
  },

  bulk: {
    activate: "Activate",
    deactivate: "Deactivate",
    moveTo: "Move to…",
    configure: "Configure",
    label: "Label",
  },

  // Device label editor (bulk assign; feeds audience label targeting).
  labels: {
    title: "Label devices",
    description:
      "Labels are key/value tags (e.g. zone=bar, floor=2). Audiences can target devices by label, so labeled fleets schedule themselves.",
    keyPlaceholder: "key (e.g. zone)",
    valuePlaceholder: "value (e.g. bar)",
    audiencesLink: "Open Audiences",
    addRow: "Add label",
    removeRow: "Remove this label row",
    removeKeysLabel: "Remove existing keys (comma-separated, optional)",
    removeKeysPlaceholder: "e.g. zone, floor",
    apply: "Apply to {count} devices",
    applied: "Labels updated",
    failed: "Could not update labels",

    // Per-device label editor (device settings dialog) + list chips.
    sectionTitle: "Labels",
    sectionHint:
      "Labels are key/value tags that audiences use to target this device for scheduling.",
    empty: "No labels on this device yet.",
    add: "Add",
    save: "Save labels",
    removeChipAria: "Remove label {key}",
    overflow: "+{count}",

    // Toolbar label filter (wired to the labelKey/labelValue list params).
    filter: {
      button: "Label",
      title: "Filter by label",
      keyLabel: "Key",
      valueLabel: "Value (optional)",
      aria: "Filter devices and locations by label",
    },
  },

  confirmDeleteDevice: "Delete device?",

  toast: {
    deviceDeleted: "Device deleted",
    deviceDeleteFailed: "Could not delete device",
    deviceActivated: "Device activated",
    deviceActivateFailed: "Could not activate device",
    bulkFailed: "Bulk action failed",
    devicesActivated: "Devices activated",
    devicesDeactivated: "Devices deactivated",
    devicesMoved: "Devices moved",
    configApplied: "Configuration applied",
    enterDeviceName: "Enter at least one device name",
    devicesCreated: {
      one: "{count} device created",
      other: "{count} devices created",
    },
  },

  bulkCreate: {
    title: "Add devices",
    namesLabel: "Device names (one per line)",
    namesPlaceholder: "Front counter\nDrive-thru\nPatio speaker",
    locationLabel: "Location",
  },

  configure: {
    title: "Configure devices",
    description: {
      one: "Apply channels and features to {count} selected device. (Use Activate / Deactivate for billing.)",
      other:
        "Apply channels and features to {count} selected devices. (Use Activate / Deactivate for billing.)",
    },
    enabledChannels: "Enabled channels",
    features: "Features",
    announcementTone: "Announcement tone",
    customTonesHint:
      "Custom tones (your own uploads) are set per device in its settings dialog.",
  },

  features: {
    liveBroadcast: "Live broadcast",
    liveAnnouncement: "Live announcement",
    remoteDesktop: "Remote desktop",
    djMode: "DJ mode (crossfade)",
  },

  tone: {
    unchanged: "Leave unchanged",
    off: "Off",
    ding: "Ding",
    chime: "Chime",
    bell: "Bell",
  },

  move: {
    title: { one: "Move {count} device", other: "Move {count} devices" },
    destination: "Destination location",
  },

  device: {
    nowPlaying: "Now Playing",
    never: "Never",
    inactive: "Inactive",
    inactiveHint:
      "Not activated. This device stays silent until you activate it (uses one device license).",
    activate: "Activate",
  },

  preview: {
    title: "Preview",
    resolvedFor: "Resolved schedule for this site:",
    noAddress: "No address",
  },

  // --- LocationDialog (location-dialog.tsx) ---
  dialog: {
    editTitle: "Edit location",
    newTitle: "New location",
    createSubmit: "Create location",
    deleteLabel: "Delete location",
    openingHoursLabel: "Opening hours",
    confirmDelete: "Delete this location? Devices here become unassigned.",
  },

  form: {
    namePlaceholder: "e.g. Downtown flagship",
    region: "Region / Market",
    regionPlaceholder: "e.g. West Coast, EMEA",
    city: "City",
    country: "Country",
    timezoneHint: "Devices at this location play schedules in this timezone.",
  },

  location: {
    updated: "Location updated",
    updateFailed: "Failed to update",
    created: "Location created",
    createFailed: "Failed to create",
    deleted: "Location deleted",
    deleteFailed: "Failed to delete",
  },

  picker: {
    placeholder: "Select location",
    createAria: "Create location",
  },

  // --- Opening-hours editor (opening-hours-editor.tsx) ---
  hours: {
    days: {
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday",
    },
    open247: "Open 24/7",
    setWeekly: "Set weekly hours",
    noHoursHint:
      "No hours configured. Players at this location can play around the clock.",
    closed: "Closed",
    nextDay: "next day",
    spillTitle: "Closes past midnight, the next morning",
    copyWeekdays: "Copy Monday to weekdays",
    copyAll: "Copy Monday to all",
    clearOpen247: "Clear (open 24/7)",
    footnote:
      "Outside these hours, music and screens at this location stop automatically. Quick Play and Takeover-priority schedules still play through closed hours.",
    footnoteOvernight:
      "Outside these hours, music and screens at this location stop automatically. Quick Play and Takeover-priority schedules still play through closed hours; a close time at or before the open time ends the next morning.",
    aria: {
      open: "{day} open",
      opensAt: "{day} opens at",
      closesAt: "{day} closes at",
    },
  },
} as const;
